import React, { useState, useRef } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Tooltip,
  Typography,
  Divider,
  MenuList,
  MenuItem,
  Checkbox,
  ListItemText,
  Popper,
  Paper,
  ClickAwayListener,
  Fab,
  useMediaQuery,
} from '@material-ui/core';
import { Icon } from '@iconify/react';
import CollectionsIcon from '@material-ui/icons/Collections';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import GrainIcon from '@material-ui/icons/Grain';
import { ZoomIn, ZoomOut } from '@material-ui/icons';
import * as actions from '../../../../../store/index';
import { setPostSurgery, setQualityOverlaysToShow } from '../../../../../store';
import { useAppliedEvaluation } from '../../../../../helpers/hooks/useEvaluation';
import { useQualityTranslation } from '../../../../../helpers/hooks/useQualityTranslation';
import { compressionInfo } from '../../QualityView/utils/qualityUtils';
import { qualityClassColor } from '../../../../../helpers/manageColors';
import { onClickChip } from '../../QualityView/hooks/useQualityHandlers';
import ProjectionFabs from '../Projections/common/ProjectionFabs';
import patch20Regular from '@iconify/icons-fluent/patch-20-regular';
import patch20Filled from '@iconify/icons-fluent/patch-20-filled';
import radioactiveIcon from '@iconify-icons/mdi/radioactive';
import blurRadial from '@iconify-icons/mdi/google-podcast';
import contrastIcon from '@iconify-icons/carbon/contrast';
import arrowCollapseVertical from '@iconify-icons/mdi/arrow-collapse-vertical';
import invertColorIcon from '@iconify-icons/mdi/invert-colors';
import WarningIcon from '@material-ui/icons/Warning';
import mammoIcon from '../../../../../resources/icons/2D.svg';
import syntheticIcon from '../../../../../resources/icons/s2D.svg';
import synthetic3DIcon from '../../../../../resources/icons/3D_s2D.svg';
import mammo3DIcon from '../../../../../resources/icons/2D3D.svg';
import pseudoSyntheticIcon from '../../../../../resources/icons/3D.svg';
import pseudoTomoIcon from '../../../../../resources/icons/3DpseudoTomo.svg';
import notAvailableImg from '../../../../../resources/icons/notAvailableImg.svg';

const useStyles = makeStyles((theme) => ({
  projection_evaluations_equip: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    height: '100%',
  },

  tooltip_text: {
    letterSpacing: '0.1rem',
    fontSize: theme.fonts.responsiveMedium,
  },

  volumeText: {
    margin: '0 6px',
    cursor: 'help',
  },

  menu_list_item: {
    display: 'flex',
    alignItems: 'center',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: theme.fonts.responsiveSmall,
    color: '#d3d3d3',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },

  popperPaper: {
    color: '#d3d3d3',
    padding: '8px',
    border: '1px solid #3e3e3e',
    borderRadius: '4px',
    boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.5)',
  },

  // Zoom button styles
  ZoomLeft: {
    zIndex: 50,
    opacity: 0,
    boxShadow: 'none',
    animation: '$appear 600ms forwards',
    background: theme.palette.accents.dark,
    height: '26px',
    width: '26px',
    minHeight: '26px',
    '&:hover': {
      backgroundColor: theme.palette.accents.main,
      boxShadow: '0px 0px 5px -1px #FFFFF',
    },
  },

  ZoomRight: {
    zIndex: 50,
    opacity: 0,
    boxShadow: 'none',
    animation: '$appear 600ms forwards',
    background: theme.palette.accents.dark,
    height: '26px',
    width: '26px',
    minHeight: '26px',
    transform: 'scaleX(-1)',
    '&:hover': {
      backgroundColor: theme.palette.accents.main,
      boxShadow: '0px 0px 5px -1px #FFFF',
    },
  },

  '@keyframes appear': {
    '70%': {
      opacity: 0,
    },
    '100%': {
      opacity: 0.8,
    },
  },

  zoomIcon: {
    color: 'white',
    fontSize: '18px',
  },

  fabs_texts: {
    fontSize: theme.fonts.responsiveXSmall,
    color: 'gray',
    textTransform: 'uppercase',
  },

  menu_item_text: {
    fontSize: theme.fonts.responsiveSmall,
    color: '#d3d3d3',
  },

  sub_div_container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    padding: '4px 0',
    minHeight: 'fit-content',
  },

  // Standard icon styles

  standardIconSmall: {
    width: '16px',
    height: '16px',
    fontSize: '16px',
  },
}));

const normalizeOptionList = (value) => {
  if (value == null || value === false) return [];
  if (Array.isArray(value)) return value.filter((item) => item && item !== 'not_evaluated');
  if (typeof value === 'string') {
    return value === '' || value === 'not_evaluated' ? [] : [value];
  }
  return [];
};

// Selectable FABs (Postop and Artifacts)
const SelectableFab = ({
  anchorRef,
  showSelect,
  options,
  selectedOptions,
  onOptionChange,
  onPopperClose,
  tooltipText,
  chipAction,
  chipIcon,
  extraStyles,
  clickableChip = true,
  tooltipAction,
  tooltipLeave,
  t,
  theme,
  classes,
  warningIcon,
  left,
}) => {
  return (
    <ClickAwayListener onClickAway={onPopperClose} mouseEvent='onClick'>
      <div>
        <div ref={anchorRef} style={{ position: 'relative' }}>
          <ProjectionFabs
            tooltipAction={tooltipAction}
            tooltipLeave={tooltipLeave}
            tooltipText={tooltipText}
            chipAction={chipAction}
            extraStyles={extraStyles}
            chipIcon={chipIcon}
            clickableChip={clickableChip}
          />
          {warningIcon && (
            <WarningIcon
              fontSize='small'
              style={{
                fill: theme.palette.status.warning,
                position: 'absolute',
                bottom: '-4px',
                left: left ? '-2px' : undefined,
                right: left ? undefined : '-2px',
                zIndex: 20,
                height: '14px',
                width: '14px',
              }}
            />
          )}
        </div>

        <Popper
          open={showSelect}
          anchorEl={anchorRef.current}
          placement='bottom-start'
          disablePortal={false}
          keepMounted={false}
          modifiers={{
            flip: {
              enabled: true,
            },
            preventOverflow: {
              enabled: true,
              boundariesElement: 'scrollParent',
            },
          }}
          style={{ zIndex: 2000 }}>
          <Paper className={classes.popperPaper}>
            <MenuList variant='menu' style={{ minWidth: '150px' }}>
              {options.map((option) => (
                <MenuItem
                  key={option}
                  className={classes.menu_list_item}
                  onClick={() => onOptionChange(option)}>
                  <Checkbox
                    size='small'
                    checked={selectedOptions?.includes(option)}
                    style={{
                      color: '#4272c9bd',
                      marginRight: '8px',
                      padding: '0px',
                    }}
                  />
                  <ListItemText
                    primary={t(`projections.${option}`) || option}
                    style={{ fontSize: '0.8rem' }}
                  />
                </MenuItem>
              ))}
            </MenuList>
          </Paper>
        </Popper>
      </div>
    </ClickAwayListener>
  );
};

const ProjectionIcons = ({
  projectionName,
  isLeft,
  zoomBtnClass,
  isZoomed,
  onZoomClick,
  invertedColor,
  setInvertedColor,
  setSecondCaptureOpen,
  secondCaptureOpen,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Store state
  const results = useSelector((state) => state.results);
  const { exam_id, projections, applied_quality, applied_density, shown_quality, shown_density } = results;
  const { qualityOverlaysToShow } = useSelector((state) => state.images);
  const { tabsIndex } = useSelector((state) => state.gui);
  const {
    qualityInRevisionMode,
    qualityInDrawingMode,
    densityInRevisionMode,
    diagnosticsInRevisionMode,
    customOpacities,
    customMicrocalc,
    localChangesQuality,
  } = useSelector((state) => state.labeling);
  const currentExam = useSelector((state) => state.examinations.currentExam);
  const density_module = useSelector((state) => state.global_settings.premium_features.density_module);
  const { compression_low, compression_high, compression_tolerance } = useSelector(
    (state) => state.global_settings,
  );
  const examinationListDrawer = useSelector((state) => state.gui.examinationListDrawer);
  const { compression_classes, contrast_classes, noise_classes, exposure_classes, artifacts_classes } =
    useSelector((state) => state.global_settings.quality_system.quality_features);

  // Local state
  const [showPostSurgerySelect, setShowPostSurgerySelect] = useState(false);
  const [showArtifactsSelect, setShowArtifactsSelect] = useState(false);
  const postSurgeryAnchorRef = useRef(null);
  const artifactsAnchorRef = useRef(null);
  const smallScreen = useMediaQuery('(max-width: 1450px)') || examinationListDrawer;

  // Variables
  const postSurgeryOptions = ['tissue_markers', 'implant', 'scar_reduction', 'postop_materials'];
  const artifactsOptions = ['exogenous_material', 'body_parts', 'technical'];

  const qualityShown = shown_quality || applied_quality;
  const densityShown = shown_density || applied_density;

  const resultQualityEvaluation = useAppliedEvaluation('quality', qualityShown, qualityInRevisionMode);
  const resultDensityEvaluation = useAppliedEvaluation('density', densityShown, densityInRevisionMode);

  // Evaluation values
  const postOpEval = normalizeOptionList(resultQualityEvaluation?.[projectionName]?.PostSurgery);
  const artifactsEval = normalizeOptionList(resultQualityEvaluation?.[projectionName]?.Artifacts);
  const contrastEval = resultQualityEvaluation?.[projectionName]?.contrast;
  const noiseEval = resultQualityEvaluation?.[projectionName]?.noise;
  const exposureEval = resultQualityEvaluation?.[projectionName]?.exposureClass;
  const hasSecondaryCaptures = results?.second_captures?.[projectionName]?.length > 0;
  const inRevisionMode = qualityInRevisionMode || densityInRevisionMode || diagnosticsInRevisionMode;

  // Handlers
  const showClips = (currentProj, shouldShow) => {
    const newLesionsToShow = {
      [`clips${currentProj.toUpperCase()}`]: shouldShow,
    };
    if (tabsIndex === 0 || (tabsIndex === 1 && !qualityInDrawingMode)) {
      dispatch(setQualityOverlaysToShow(newLesionsToShow));
    }
  };

  const postopHandler = () => {
    setShowPostSurgerySelect(!showPostSurgerySelect);
  };

  const handlePostSurgeryOptionChange = (option) => {
    let newOptions = [...(postOpEval || [])];
    if (newOptions.includes(option)) {
      newOptions = newOptions.filter((item) => item !== option);
    } else {
      newOptions.push(option);
    }
    dispatch(setPostSurgery(currentExam, projectionName, newOptions));
  };

  const handlePopperClose = () => {
    setShowPostSurgerySelect(false);
  };

  const artifactsHandler = () => {
    if (qualityInRevisionMode) {
      setShowArtifactsSelect(!showArtifactsSelect);
    } else return;
  };

  const handleArtifactsOptionChange = (option) => {
    let newOptions = [...artifactsEval];
    if (newOptions.includes(option)) {
      newOptions = newOptions.filter((item) => item !== option);
    } else {
      newOptions.push(option);
    }

    dispatch(actions.setQualityLocalChanges(true));
    dispatch(actions.updateCustomQualityFeature(projectionName, 'Artifacts', newOptions));
  };

  const handleArtifactsPopperClose = () => {
    setShowArtifactsSelect(false);
  };

  // Helper function to generate tooltip content
  const generateTooltipContent = (selectedOptions, optionsTitle, noOptionsText, setText, warningText) => {
    const options = normalizeOptionList(selectedOptions);
    return (
      <>
        {warningText && (
          <>
            <Typography
              component='span'
              style={{
                letterSpacing: '0.1rem',
                fontSize: theme.fonts.responsiveMedium,
                fontWeight: setText ? 'bold' : 'normal',
              }}>
              {warningText}
            </Typography>
            <Divider style={{ margin: '3%' }} />
          </>
        )}
        {noOptionsText && (
          <Typography
            component='span'
            style={{
              letterSpacing: '0.1rem',
              fontSize: theme.fonts.responsiveMedium,
              fontWeight: setText ? 'bold' : 'normal',
            }}>
            {options.length > 0 ? optionsTitle : noOptionsText}
          </Typography>
        )}

        {setText && <Divider style={{ margin: '3%' }} />}
        {options.length > 0 ? (
          <>
            <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
              {options.map((item, idx) => (
                <li key={idx}>
                  <Typography
                    component='span'
                    style={{
                      letterSpacing: '0.1rem',
                      fontSize: theme.fonts.responsiveMedium,
                      display: 'inline',
                    }}
                    color='inherit'>
                    {t(`projections.${item}`)}
                  </Typography>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <Typography
            component='span'
            style={{
              letterSpacing: '0.1rem',
              fontSize: theme.fonts.responsiveMedium,
              display: 'flex',
              alignItems: 'center',
            }}
            color='inherit'>
            {options.length > 0 && <TouchAppIcon fontSize='medium' />}
            {setText}
          </Typography>
        )}
      </>
    );
  };

  const getImageType = (analyzed_3d, modality) => {
    let tooltip = 'N/A';
    let iconSymbol = (
      <img
        src={notAvailableImg}
        alt='not_available'
        className={classes.standardIconSmall}
        style={{ height: '10px' }}
      />
    );
    if (modality === 'mammo' && analyzed_3d === 0) {
      // Mammo (2D)
      iconSymbol = <img src={mammoIcon} alt='2D' className={classes.standardIconSmall} />;
      tooltip = t('projections.mammo_2D');
    }
    if (modality === 'mammo' && analyzed_3d === 1) {
      // Mammo (3D)
      iconSymbol = <img src={mammo3DIcon} alt='3D+2D' className={classes.standardIconSmall} />;
      tooltip = t('projections.mammo_3D');
    } else if (modality === 'tomo' && analyzed_3d === 0) {
      // Synthetic 2D;
      iconSymbol = <img src={syntheticIcon} alt='s2D' className={classes.standardIconSmall} />;
      tooltip = t('projections.s2D');
    } else if (modality === 'tomo' && analyzed_3d === 1) {
      // Synthetic 2D + 3D Tomosynthesis ';
      iconSymbol = <img src={synthetic3DIcon} alt='3D+s2D' className={classes.standardIconSmall} />;
      tooltip = t('projections.synthetic_2D_3D');
    } else if ((modality === 'pseudo_synthetic' || modality === 'pseudo_tomo') && analyzed_3d === 1) {
      //3D Tomosynthesis
      iconSymbol = <img src={pseudoSyntheticIcon} alt='3D' className={classes.standardIconSmall} />;
      tooltip = t('projections.pseudo_tomo');
    }

    return { tooltip, iconSymbol };
  };

  return (
    <div className={classes.projection_evaluations_equip}>
      <div
        className={classes.sub_div_container}
        style={{
          height: '38%',
          justifyContent: smallScreen ? 'flex-start' : 'space-between',
          gap: smallScreen ? '4px' : undefined,
        }}>
        {!smallScreen && (
          <Typography className={classes.fabs_texts}>{t('projections.image_title')}</Typography>
        )}

        {/* Zoom Button */}
        {
          <Tooltip
            arrow
            placement='left'
            title={
              <Typography className={classes.tooltip_text}>
                {isZoomed ? t('projections.zoom_out') : t('projections.zoom_in')}
              </Typography>
            }>
            <Fab
              size='small'
              className={classes[zoomBtnClass]}
              onClick={() => onZoomClick && onZoomClick(projectionName)}
              style={{
                backgroundColor: smallScreen ? 'transparent' : theme.palette.accents.dark,
              }}>
              {isZoomed ? (
                <ZoomOut
                  className={classes.zoomIcon}
                  style={{ fill: smallScreen ? 'rgb(54 141 141)' : 'white' }}
                />
              ) : (
                <ZoomIn
                  className={classes.zoomIcon}
                  style={{ fill: smallScreen ? 'rgb(54 141 141)' : 'white' }}
                />
              )}
            </Fab>
          </Tooltip>
        }

        {/*  Display 2D 3D icon */}
        <ProjectionFabs
          chipIcon={
            getImageType(projections?.[projectionName]?.analyzed_3d, projections?.[projectionName]?.modality)
              .iconSymbol
          }
          evaluationClass={null}
          tooltipText={
            <Typography component='span' className={classes.tooltip_text}>
              {
                getImageType(
                  projections?.[projectionName]?.analyzed_3d,
                  projections?.[projectionName]?.modality,
                ).tooltip
              }
            </Typography>
          }
          clickableChip={false}
          extraStyles={{
            background: smallScreen ? 'transparent' : theme.palette.accents.dark,
          }}
        />

        {/* Invert Color */}
        <ProjectionFabs
          chipIcon={
            <Icon
              icon={invertColorIcon}
              className={classes.standardIconSmall}
              color={invertedColor?.[projectionName] ? 'white' : '#3e8585'}
            />
          }
          evaluationClass={null}
          tooltipText={
            <Typography component='span' className={classes.tooltip_text}>
              {t('projections.invert')}
            </Typography>
          }
          chipAction={() => {
            setInvertedColor({
              ...invertedColor,
              [projectionName]: !invertedColor?.[projectionName],
            });
          }}
          clickableChip={true}
          extraStyles={{
            background: smallScreen ? 'transparent' : 'rgb(255 255 255 / 12%)',
          }}
        />

        {/* Secondary captures */}
        <ProjectionFabs
          chipIcon={
            <CollectionsIcon
              className={classes.standardIconSmall}
              style={{
                fill: inRevisionMode
                  ? 'gray'
                  : smallScreen
                    ? hasSecondaryCaptures
                      ? 'rgb(54 141 141)'
                      : 'gray'
                    : hasSecondaryCaptures
                      ? '#e5e5e5'
                      : 'gray',
              }}
            />
          }
          evaluationClass={hasSecondaryCaptures ? 'rgb(54 141 141)' : null}
          tooltipText={
            <Typography className={classes.tooltip_text}>
              {hasSecondaryCaptures ? t('ResultView.secondCaptures') : t('ResultView.no_second_captures')}
            </Typography>
          }
          chipAction={() => {
            hasSecondaryCaptures && !inRevisionMode && setSecondCaptureOpen(projectionName);
          }}
          clickableChip={hasSecondaryCaptures && !inRevisionMode ? true : false}
          extraStyles={{
            background: smallScreen
              ? 'transparent'
              : hasSecondaryCaptures
                ? theme.palette.accents.dark
                : '#80808029',
            cursor: hasSecondaryCaptures && !inRevisionMode ? 'pointer' : 'help',
          }}
        />
      </div>

      {!qualityInDrawingMode && (
        <>
          <div
            className={classes.sub_div_container}
            style={{
              height: '22%',
              marginTop: '6px',
              justifyContent: smallScreen ? 'flex-start' : 'space-between',
              gap: smallScreen ? '4px' : undefined,
            }}>
            {!smallScreen && (
              <Typography className={classes.fabs_texts}>{t('projections.context_title')}</Typography>
            )}

            {/* POSTOP CTA Fab*/}
            <SelectableFab
              anchorRef={postSurgeryAnchorRef}
              showSelect={showPostSurgerySelect}
              options={postSurgeryOptions}
              selectedOptions={postOpEval || []}
              onOptionChange={handlePostSurgeryOptionChange}
              onPopperClose={handlePopperClose}
              t={t}
              theme={theme}
              classes={classes}
              tooltipText={generateTooltipContent(
                postOpEval || [],
                t('projections.postSurgeryOptions'),
                t('projections.no_postSurgery'),
                t('ResultView.set_operated'),
              )}
              chipAction={() => postopHandler()}
              extraStyles={{
                cursor: 'pointer',
                // border: smallScreen
                //   ? '1px solid transparent'
                //   : postOpEval && postOpEval.length > 0
                //   ? `1px solid ${theme.palette.projections.blue}`
                //   : '1px solid gray',
                background: smallScreen
                  ? 'transparent'
                  : postOpEval && postOpEval.length > 0
                    ? `${theme.palette.projections.blue}80`
                    : '#80808029',
              }}
              chipIcon={
                <LocalHospitalIcon
                  className={classes.standardIconSmall}
                  style={{
                    fill:
                      postOpEval && postOpEval.length > 0
                        ? smallScreen
                          ? `${theme.palette.projections.blue}`
                          : '#e5e5e5'
                        : 'gray',
                  }}
                />
              }
              clickableChip={true}
              tooltipAction={(shouldShow) =>
                postOpEval.includes('tissue_markers') && showClips(projectionName, shouldShow)
              }
              tooltipLeave={(shouldShow) =>
                postOpEval.includes('tissue_markers') && showClips(projectionName, shouldShow)
              }
            />

            {/* ARTIFACTS Fab */}
            <SelectableFab
              anchorRef={artifactsAnchorRef}
              showSelect={showArtifactsSelect}
              options={artifactsOptions}
              selectedOptions={artifactsEval || []}
              onOptionChange={handleArtifactsOptionChange}
              onPopperClose={handleArtifactsPopperClose}
              t={t}
              theme={theme}
              classes={classes}
              tooltipText={generateTooltipContent(
                artifactsEval || [],
                t('projections.artifactsOptions'),
                t('projections.no_artifacts'),
                qualityInRevisionMode ? t('ResultView.set_artifacts') : null,
                !qualityInRevisionMode && artifactsEval.length > 0 && t('projections.artifacts_warning'),
              )}
              chipAction={() => artifactsHandler()}
              extraStyles={{
                cursor: qualityInRevisionMode ? 'pointer' : 'help',
                background: smallScreen
                  ? 'transparent'
                  : artifactsEval.length > 0
                    ? `${theme.palette.projections.blue}80`
                    : '#80808029',
              }}
              chipIcon={
                <GrainIcon
                  className={classes.standardIconSmall}
                  style={{
                    fill:
                      artifactsEval.length > 0
                        ? smallScreen
                          ? `${theme.palette.projections.blue}`
                          : '#e5e5e5'
                        : 'gray',
                  }}
                />
              }
              clickableChip={true}
              warningIcon={!qualityInRevisionMode && artifactsEval.length > 0 && !smallScreen}
              left={projectionName === 'lcc' || projectionName === 'lmlo' ? true : false}
            />
          </div>
        </>
      )}

      {!qualityInDrawingMode && (
        <>
          <div
            className={classes.sub_div_container}
            style={{
              height: '40%',
              marginTop: '6px',
              justifyContent: smallScreen ? 'flex-start' : 'space-between',
              gap: smallScreen ? '4px' : undefined,
            }}>
            {/* Equip image evaluation */}
            {(tabsIndex === 1 || (tabsIndex === 2 && qualityInRevisionMode)) && (
              <>
                {!smallScreen && (
                  <Typography className={classes.fabs_texts}>{t('projections.qa_title')}</Typography>
                )}
                {/* COMPRESSION Fab */}
                {compression_classes?.length > 0 && (
                  <ProjectionFabs
                    chipIcon={
                      <Icon
                        icon={arrowCollapseVertical}
                        className={classes.standardIconSmall}
                        color={qualityClassColor(
                          resultQualityEvaluation?.[projectionName]?.compressionClass,
                          theme,
                        )}
                      />
                    }
                    evaluationClass={resultQualityEvaluation?.[projectionName]?.compressionClass}
                    tooltipText={compressionInfo(
                      resultQualityEvaluation?.[projectionName]?.compressionClass,
                      projections?.[projectionName]?.compression,
                      compression_low,
                      compression_high,
                      compression_tolerance,
                      theme,
                      t,
                    )}
                    chipAction={() => {
                      onClickChip('compressionClass', projectionName, compression_classes, dispatch, actions);
                    }}
                  />
                )}

                {/* CONTRAST Fab */}
                {contrast_classes?.length > 0 && (
                  <ProjectionFabs
                    evaluationClass={contrastEval}
                    tooltipText={
                      <Typography component='span' className={classes.tooltip_text}>
                        {t('ResultView.contrast')}: {useQualityTranslation(contrastEval, t)}
                      </Typography>
                    }
                    extraStyles={{
                      color: contrastEval ? qualityClassColor(contrastEval, theme) : '#3e3e3e',
                    }}
                    chipIcon={<Icon className={classes.standardIconSmall} icon={contrastIcon} />}
                    chipAction={() => {
                      onClickChip('contrast', projectionName, contrast_classes, dispatch, actions);
                    }}
                  />
                )}

                {/* NOISE Fab */}
                {noise_classes?.length > 0 && (
                  <ProjectionFabs
                    evaluationClass={noiseEval}
                    tooltipText={
                      <Typography component='span' className={classes.tooltip_text}>
                        {t('ResultView.noise')}: {useQualityTranslation(noiseEval, t)}
                      </Typography>
                    }
                    extraStyles={{
                      color: noiseEval ? qualityClassColor(noiseEval, theme) : '#3e3e3e',
                    }}
                    chipIcon={<Icon className={classes.standardIconSmall} icon={blurRadial} />}
                    chipAction={() => {
                      onClickChip('noise', projectionName, noise_classes, dispatch, actions);
                    }}
                  />
                )}

                {/* EXPOSURE Fab */}
                {exposure_classes?.length > 0 && (
                  <ProjectionFabs
                    evaluationClass={exposureEval}
                    tooltipText={
                      <Typography component='span' className={classes.tooltip_text}>
                        {t('ResultView.exposure')}: {useQualityTranslation(exposureEval, t)}
                      </Typography>
                    }
                    extraStyles={{
                      color: exposureEval ? qualityClassColor(exposureEval, theme) : '#3e3e3e',
                    }}
                    chipIcon={<Icon className={classes.standardIconSmall} icon={radioactiveIcon} />}
                    chipAction={() => {
                      onClickChip('exposureClass', projectionName, exposure_classes, dispatch, actions);
                    }}
                  />
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectionIcons;
